import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Users, Video } from 'lucide-react';
import { comunidadService } from '../services/comunidadService';
import { useUIStore } from '../store/uiStore';
import { useAuthStore } from '../store/authStore';
import { EstadoVacio } from '../components/EstadoVacio';
import PostCard from '../components/comunidad/PostCard';
import { PostComunidad, PostDTO, mensajeDeError } from '../types/comunidad';

const LIMITE_TITULO = 200;
const LIMITE_CONTENIDO = 2000;

type Campos = 'titulo' | 'contenido' | 'categoria';
type Errores = Partial<Record<Campos, string>>;

const POST_VACIO: PostDTO = { titulo: '', contenido: '', categoria: '' };

/**
 * Comunidad y Foros: el feed de posts de tu consultorio.
 *
 * REVISIÓN DE LA CREACIÓN DE POST
 * -------------------------------
 * Antes el formulario solo avisaba con un toast y ya:
 *
 * - Sin estado de "publicando": el boton seguia activo y un doble toque
 *   creaba dos posts.
 * - Sin validacion junto al campo: tocabas "Publicar" y habia que
 *   descubrir que faltaba el titulo en un aviso que se iba.
 * - Sin acuse de recibo: el post aparecia arriba de la lista y la
 *   pagina se quedaba donde estaba, sin señal de cual era el tuyo.
 * - `PostCard` recibia `any`, y `handleLike` reemplazaba el post entero
 *   con la respuesta del backend —que viene sin autor ni contador de
 *   respuestas—, asi que el postIndicado perdia su nombre y su numero de
 *   comentarios despues del primer like.
 */
export default function ComunidadPage() {
  const { addToast } = useUIStore();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const [posts, setPosts] = useState<PostComunidad[]>([]);
  const [categorias, setCategorias] = useState<string[]>([]);
  const [selectedCategoria, setSelectedCategoria] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const [showNewPost, setShowNewPost] = useState(false);
  const [newPost, setNewPost] = useState<PostDTO>(POST_VACIO);
  const [errores, setErrores] = useState<Errores>({});
  const [publicando, setPublicando] = useState(false);

  /** Id del post recien publicado: se resalta y se lleva la vista a el. */
  const [recienPublicado, setRecienPublicado] = useState<number | null>(null);
  /** Post con el like en vuelo: deshabilita SOLO ese boton. */
  const [liking, setLiking] = useState<number | null>(null);

  /** PSICOLOGO/ADMIN ven todos los posts y los moderan. El backend
   *  comprueba el rol en cada endpoint; esto solo decide que botones se
   *  pintan, para no ofrecer lo que el servidor va a rechazar. */
  const esModerador = user?.rol === 'PSICOLOGO' || user?.rol === 'ADMIN';

  useEffect(() => {
    let vigente = true;

    (async () => {
      try {
        const [postsData, categoriasData] = await Promise.all([
          comunidadService.getPosts(selectedCategoria || undefined),
          comunidadService.getCategorias(),
        ]);
        if (!vigente) return;
        setPosts(postsData);
        setCategorias(categoriasData);
      } catch {
        if (!vigente) return;
        addToast({
          type: 'error',
          title: 'Error',
          message: 'No se pudieron cargar los posts',
        });
      } finally {
        if (vigente) setIsLoading(false);
      }
    })();

    return () => {
      vigente = false;
    };
  }, [selectedCategoria, addToast]);

  /** Al publicar: scrolls al post nuevo y se marca unos segundos. */
  useEffect(() => {
    if (recienPublicado === null) return;

    const ver = window.setTimeout(() => {
      document
        .getElementById(`post-${recienPublicado}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    const apagar = window.setTimeout(() => setRecienPublicado(null), 4000);

    return () => {
      window.clearTimeout(ver);
      window.clearTimeout(apagar);
    };
  }, [recienPublicado]);

  const abrirNuevoPost = () => {
    setShowNewPost(true);
    window.setTimeout(() => {
      document.getElementById('nuevo-post')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  };

  const validar = (datos: PostDTO): Errores => {
    const err: Errores = {};
    if (!datos.titulo.trim()) err.titulo = 'Ponle un título: es lo primero que se lee.';
    if (!datos.contenido.trim()) err.contenido = 'Cuenta algo: el post va vacío.';
    if (!datos.categoria) err.categoria = 'Elige una categoría para que cada quien lo encuentre.';
    return err;
  };

  const handleSubmitPost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (publicando) return;

    const err = validar(newPost);
    setErrores(err);
    if (Object.keys(err).length > 0) {
      // En movil el aviso sale ahi mismo, pero el teclado sigue abierto
      // donde estaba: el foco salta al primer campo vacio para no tener
      // que buscarlo a ojo.
      const primero = (['titulo', 'categoria', 'contenido'] as Campos[]).find((c) => err[c]);
      if (primero) document.getElementById(`post-${primero}`)?.focus();
      return;
    }

    setPublicando(true);
    try {
      const creado = await comunidadService.createPost({
        titulo: newPost.titulo.trim(),
        contenido: newPost.contenido.trim(),
        categoria: newPost.categoria,
      });

      setPosts((prev) => [creado, ...prev]);
      setNewPost(POST_VACIO);
      setShowNewPost(false);
      setRecienPublicado(creado.id);
      addToast({
        type: 'success',
        title: 'Post publicado',
        message: 'Ya aparece arriba para los demás de tu consultorio.',
      });
    } catch (error) {
      // El backend explica por qué rechaza (p. ej. "no tienes un
      // profesional asignado en INTUS"). Un mensaje genérico deja al
      // usuario sin saber si falló la validación, los permisos o la red.
      addToast({
        type: 'error',
        title: 'No se pudo publicar',
        message: mensajeDeError(error, 'No se pudo crear el post'),
      });
    } finally {
      setPublicando(false);
    }
  };

  const handleLike = async (postId: number) => {
    if (liking !== null) return;
    setLiking(postId);

    // Optimista: el numero sube en el mismo toque. Un contador que no se
    // mueve durante la ida y vuelta a la red se lee como "no?to", y en
    // datos de apoyo es justo el gesto que se pierde. Si el backend lo
    // rechaza, el numero vuelve a su valor y se avisa; si lo acepta, manda
    // el suyo, que es la cifra real.
    setPosts((prev) =>
      prev.map((p) => (p.id === postId ? { ...p, likes: p.likes + 1 } : p)),
    );

    try {
      const actualizado = await comunidadService.addLike(postId);
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, likes: actualizado.likes } : p)),
      );
    } catch (error) {
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, likes: Math.max(0, p.likes - 1) } : p)),
      );
      addToast({
        type: 'error',
        title: 'No se pudo apoyar',
        message: mensajeDeError(error, 'No se pudo registrar tu apoyo'),
      });
    } finally {
      setLiking(null);
    }
  };

  const handleReportar = async (postId: number) => {
    try {
      await comunidadService.reportPost(postId);
      addToast({
        type: 'success',
        title: 'Post reportado',
        message: 'Gracias por avisar. El equipo lo revisa.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'No se pudo reportar',
        message: mensajeDeError(error, 'No se pudo enviar el reporte'),
      });
    }
  };

  const handleEliminar = async (postId: number) => {
    try {
      await comunidadService.deletePost(postId);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      addToast({
        type: 'success',
        title: 'Post borrado',
        message: 'Ya no aparece en la comunidad.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'No se pudo borrar',
        message: mensajeDeError(error, 'Solo puedes borrar tus propios posts'),
      });
    }
  };

  const handleOcultar = async (postId: number) => {
    try {
      await comunidadService.moderatePost(postId, 'PENDIENTE');
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      addToast({
        type: 'success',
        title: 'Post oculto',
        message: 'Sale de la lista mientras lo revisas.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'No se pudo ocultar',
        message: mensajeDeError(error, 'No se pudo moderar el post'),
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="mb-2 text-2xl font-bold md:text-3xl">Comunidad y Foros</h1>
          <p className="text-sm text-v-on-surface-variant md:text-base">
            Comparte experiencias y participa en foros de discusión
          </p>
          {/* El foro es abierto: antes cada paciente solo veía los posts de su
              consultorio. Decirlo evita que alguien seprecie de que "aparezcan"
              posts de otros centros. */}
          <p className="mt-1 text-xs text-v-on-surface-variant">
            Visible para todos los pacientes y profesionales de Vita360
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/videos')}
            /* Container + su on-* , no `bg-v-secondary`: en claro el texto
               oscuro sobre #2C93BB da 4.17:1 y en oscuro 1.69:1. Es el mismo
               par que usa VideosPage para este destino. */
            className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-v-primary-container px-4 text-v-on-primary-container transition-colors hover:bg-v-secondary-container"
          >
            <Video className="h-4 w-4" />
            <span className="hidden sm:inline">Ver Videos</span>
            <span className="sm:hidden">Videos</span>
          </button>

          {/* En movil publicar vive en el boton flotante de abajo: el de
              la cabecera se va con el scroll y obliga a subir hasta el
              principio para escribir. Dos CTA identicos compitiendo por
              el mismo pulgar es peor que uno. */}
          <button
            type="button"
            onClick={abrirNuevoPost}
            className="hidden min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-primary-foreground transition-colors hover:bg-primary/90 md:inline-flex"
          >
            <Plus className="h-4 w-4" />
            <span>Nuevo Post</span>
          </button>
        </div>
      </div>

      {/* Publicar siempre a mano: fijo sobre la barra inferior, a la
          altura del pulgar. Se oculta con el formulario abierto para no
          competir con el "Publicar" que ya esta en pantalla. */}
      {!showNewPost && (
        <button
          type="button"
          onClick={abrirNuevoPost}
          aria-label="Publicar un post nuevo"
          className="no-tap-highlight fab-barra fixed right-4 z-30 flex min-h-12 items-center gap-2 rounded-pill bg-primary px-5 font-semibold text-primary-foreground shadow-lg transition-transform active:scale-95 md:hidden"
        >
          <Plus className="h-5 w-5" />
          Publicar
        </button>
      )}

      {/* Category Filter. El carril se desliza con el dedo: `scrollbar-hide`
          evita que la barra corte los chips a la mitad en Android (en Safari
          no sale nunca). */}
      <div className="scrollbar-hide -mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1">
        <BotonCategoria
          texto="Todos"
          activa={selectedCategoria === ''}
          onClick={() => setSelectedCategoria('')}
        />
        {categorias.map((categoria) => (
          <BotonCategoria
            key={categoria}
            texto={categoria}
            activa={selectedCategoria === categoria}
            onClick={() => setSelectedCategoria(categoria)}
          />
        ))}
      </div>

      {/* New Post Form */}
      {showNewPost && (
        <section
          id="nuevo-post"
          aria-label="Crear nuevo post"
          className="rounded-xl border border-v-outline bg-card p-4 md:p-6"
        >
          <h3 className="mb-4 text-lg font-semibold">Crear Nuevo Post</h3>

          <form onSubmit={handleSubmitPost} noValidate className="space-y-4">
            <div>
              <label htmlFor="post-titulo" className="mb-2 block text-sm font-medium">
                Título
              </label>
              <input
                id="post-titulo"
                type="text"
                value={newPost.titulo}
                onChange={(e) => {
                  setNewPost({ ...newPost, titulo: e.target.value });
                  if (errores.titulo) setErrores({ ...errores, titulo: undefined });
                }}
                maxLength={LIMITE_TITULO}
                aria-invalid={Boolean(errores.titulo)}
                aria-describedby={errores.titulo ? 'post-titulo-error' : undefined}
                className={`w-full rounded-lg border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary md:px-4 md:py-2 md:text-base ${
                  errores.titulo ? 'border-v-error' : 'border-v-outline'
                }`}
                placeholder="Título de tu post..."
              />
              <div className="mt-1 flex items-center justify-between gap-2">
                {errores.titulo ? (
                  <p id="post-titulo-error" className="text-xs font-medium text-v-error">
                    {errores.titulo}
                  </p>
                ) : (
                  <span />
                )}
                <span className="text-xs text-v-on-surface-variant">
                  {newPost.titulo.length}/{LIMITE_TITULO}
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="post-categoria" className="mb-2 block text-sm font-medium">
                Categoría
              </label>
              <select
                id="post-categoria"
                value={newPost.categoria}
                onChange={(e) => {
                  setNewPost({ ...newPost, categoria: e.target.value });
                  if (errores.categoria) setErrores({ ...errores, categoria: undefined });
                }}
                aria-invalid={Boolean(errores.categoria)}
                aria-describedby={errores.categoria ? 'post-categoria-error' : undefined}
                className={`w-full rounded-lg border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary md:px-4 md:py-2 md:text-base ${
                  errores.categoria ? 'border-v-error' : 'border-v-outline'
                }`}
              >
                <option value="">Seleccionar categoría</option>
                {categorias.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              {errores.categoria && (
                <p id="post-categoria-error" className="mt-1 text-xs font-medium text-v-error">
                  {errores.categoria}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="post-contenido" className="mb-2 block text-sm font-medium">
                Contenido
              </label>
              <textarea
                id="post-contenido"
                value={newPost.contenido}
                onChange={(e) => {
                  setNewPost({ ...newPost, contenido: e.target.value });
                  if (errores.contenido) setErrores({ ...errores, contenido: undefined });
                }}
                aria-invalid={Boolean(errores.contenido)}
                aria-describedby={errores.contenido ? 'post-contenido-error' : undefined}
                maxLength={LIMITE_CONTENIDO}
                rows={4}
                className={`w-full resize-none rounded-lg border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary md:px-4 md:py-2 md:text-base ${
                  errores.contenido ? 'border-v-error' : 'border-v-outline'
                }`}
                placeholder="Comparte tu experiencia..."
              />
              <div className="mt-1 flex items-center justify-between gap-2">
                {errores.contenido ? (
                  <p id="post-contenido-error" className="text-xs font-medium text-v-error">
                    {errores.contenido}
                  </p>
                ) : (
                  <span />
                )}
                <span className="text-xs text-v-on-surface-variant">
                  {newPost.contenido.length}/{LIMITE_CONTENIDO}
                </span>
              </div>
            </div>

            <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
              <button
                type="submit"
                disabled={publicando}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary py-2 font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {publicando && <Loader2 className="h-4 w-4 animate-spin" />}
                {publicando ? 'Publicando…' : 'Publicar'}
              </button>
              <button
                type="button"
                onClick={() => setShowNewPost(false)}
                disabled={publicando}
                className="rounded-lg border border-v-outline px-4 py-2 transition-colors hover:bg-black/5 disabled:opacity-60"
              >
                Cancelar
              </button>
            </div>
          </form>
        </section>
      )}

      {/* Posts */}
      {isLoading ? (
        <Esqueletos />
      ) : posts.length === 0 ? (
        <EstadoVacio
          icono={Users}
          container="#DCFCE7"
          ink="#166534"
          titulo="Todavía no hay posts aquí"
          descripcion="Cuando alguien comparta algo en esta categoría aparecerá aquí. Tú también puedes ser el primero."
          accion="Escribir el primer post"
          onAccion={abrirNuevoPost}
        />
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              usuarioId={user?.id}
              esModerador={esModerador}
              recienPublicado={recienPublicado === post.id}
              liking={liking === post.id}
              onLike={handleLike}
              onReportar={handleReportar}
              onEliminar={handleEliminar}
              onOcultar={handleOcultar}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BotonCategoria({
  texto,
  activa,
  onClick,
}: {
  texto: string;
  activa: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      className={`inline-flex min-h-11 items-center whitespace-nowrap rounded-full px-4 text-sm transition-colors ${
        activa
          ? 'bg-primary font-semibold text-primary-foreground'
          : 'bg-v-background text-v-on-surface-variant hover:bg-v-secondary-container'
      }`}
    >
      {texto}
    </button>
  );
}

/**
 * Esqueletos en vez de un spinner de pagina entera: el filtro de
 * categorias y el boton de "Nuevo Post" siguen utilizables mientras
 * llegan los posts, y el hueco tiene la forma del contenido que va a
 * aparecer en vez de un circulo girando en el centro.
 */
function Esqueletos() {
  return (
    <div className="space-y-4" aria-hidden>
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-xl border border-v-outline bg-card p-4 md:p-6">
          <div className="mb-4 flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-v-background" />
            <div className="space-y-2">
              <div className="h-3 w-32 animate-pulse rounded bg-v-background" />
              <div className="h-2.5 w-16 animate-pulse rounded bg-v-background" />
            </div>
          </div>
          <div className="mb-2 h-4 w-3/4 animate-pulse rounded bg-v-background" />
          <div className="mb-1 h-3 w-full animate-pulse rounded bg-v-background" />
          <div className="h-3 w-5/6 animate-pulse rounded bg-v-background" />
        </div>
      ))}
    </div>
  );
}
